import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";

export function buildSessionStatusStyles(p) {
  return {
    COMPLETED:   { ...p.green, label: "Completed" },
    ERROR:       { ...p.red, label: "Error" },
    MAX_ITERATIONS_REACHED: { ...p.amber, label: "Max Iterations" },
    IN_PROGRESS: { ...p.blue, label: "In Progress" },
    WAITING_FOR_SCHEDULED_MESSAGE: { ...p.purple, label: "Scheduled" },
    WAITING_FOR_FORM_SUBMISSION: { ...p.purple, label: "Waiting for form" },
    WAITING_FOR_REVIEW:    { ...p.amberAlt, label: "Under review" },
    SESSION_UNDER_REVIEW:  { ...p.amberAlt, label: "Under review" },
    HUMAN_TAKEOVER: { ...p.teal, label: "Human takeover" },
  };
}

export const SIDEBAR_DEFAULT_WIDTH = 260;
export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 560;
export const CHAT_AREA_MIN_WIDTH = 420;
export const SIDEBAR_RESIZE_HANDLE_WIDTH = 6;
export const TASK_QUEUE_DEFAULT_WIDTH = 420;
export const TASK_QUEUE_MIN_WIDTH = 320;
export const TASK_QUEUE_MAX_WIDTH = 640;
export const TASK_QUEUE_RESIZE_HANDLE_WIDTH = 6;
export const STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW";
export const STATUS_SESSION_UNDER_REVIEW = "SESSION_UNDER_REVIEW";
export const DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE =
  "This conversation is currently under review. Please wait for the reviewer to finish.";
export const TASK_QUEUE_AUTO_OPEN_EVENT_TYPES = new Set([
  "task_queue_created",
  "task_started",
  "task_queue_completed",
  "task_queue_cancelled",
  "task_queue_recovered",
  "task_queue_paused_for_error",
]);

export function parsePublicErrorPayload(error) {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object") {
    return responseData;
  }
  if (typeof responseData === "string") {
    try {
      return JSON.parse(responseData);
    } catch (_error) {
      return null;
    }
  }
  return null;
}

export function parsePublicErrorMessage(error, fallback) {
  const payload = parsePublicErrorPayload(error);
  if (payload && typeof payload === "object") {
    return payload.error || payload.message || fallback;
  }
  const raw = error?.response?.data;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return fallback;
}

export function parseMaybeJsonPayload(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }
  if (typeof value === "object") return value;
  return null;
}

export function getApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return 0;
  return Number(responseOrError?.status || responseOrError?.response?.status || 0);
}

export function getApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return null;
  return parseMaybeJsonPayload(responseOrError?.data ?? responseOrError?.response?.data);
}

export function parseTaskQueueErrorMessage(responseOrError, fallbackMessage) {
  const payload = getApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    const queueError = payload?.error;
    if (queueError && typeof queueError === "object") {
      if (typeof queueError.message === "string" && queueError.message.trim()) {
        return queueError.message.trim();
      }
      if (typeof queueError.code === "string" && queueError.code.trim()) {
        return queueError.code.trim();
      }
    }
    if (typeof queueError === "string" && queueError.trim()) {
      return queueError.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

export function queueUiIdempotencyKey() {
  return `task-queue-ui-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const PanelRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100%",
  width: "100%",
  minWidth: 0,
  backgroundColor: theme.palette.background.paper,
  overflowY: "hidden",
  overflowX: "auto",
}));

export const Sidebar = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.subtle,
}));

export const SidebarList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.divider} transparent`,
}));

export const ChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflowY: "hidden",
  overflowX: "auto",
  backgroundColor: theme.palette.background.paper,
}));

export function normalizeChatUiPolicy(chatUiPolicy) {
  return {
    showSystemPromptInChat: chatUiPolicy?.showSystemPromptInChat !== false,
    showToolCallsAndOutputsInChat:
      chatUiPolicy?.showToolCallsAndOutputsInChat !== false,
    showInterAgentMessagesInChat:
      chatUiPolicy?.showInterAgentMessagesInChat !== false,
    showWorkflowProvenanceInChat:
      chatUiPolicy?.showWorkflowProvenanceInChat !== false,
    showSchedulingActivityInChat:
      chatUiPolicy?.showSchedulingActivityInChat !== false,
    showSessionOperationalMetaInChat:
      chatUiPolicy?.showSessionOperationalMetaInChat !== false,
    showSupervisorControlMessagesInChat:
      chatUiPolicy?.showSupervisorControlMessagesInChat !== false,
    showSharedSessionsSidebar:
      chatUiPolicy?.showSharedSessionsSidebar !== false,
    enableHumanTakeoverMode: chatUiPolicy?.enableHumanTakeoverMode === true,
    showTaskQueuePlannerInChat:
      chatUiPolicy?.showTaskQueuePlannerInChat === true,
    forceUserNameAsYou: chatUiPolicy?.forceUserNameAsYou === true,
    compactToolCalls: chatUiPolicy?.compactToolCalls === true,
    publicUserDisplayName: chatUiPolicy?.publicUserDisplayName || "",
  };
}

export function getClickableAgentAvatarSx(avatarClick) {
  if (!avatarClick) return {};
  return {
    cursor: "pointer",
    transition:
      "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(24, 24, 27, 0.2)",
      filter: "brightness(0.98)",
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 1px 4px rgba(24, 24, 27, 0.16)",
    },
  };
}

export function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatScheduleTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  if (diffMs < 0) return "overdue";
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `in ${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `in ${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `in ${diffDay}d`;
}
